import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'
import { QUANTUM_COLORS } from './constants.js'
import { getChartContext } from './context.js'

// Badge del dashboard: tarjeta paper con header arrastrable (molde
// supertrend_quantum/Badge.jsx). Toda lectura de `result` va con guardas:
// el badge nunca debe lanzar con result null/undefined/parcial.

function formatPrice(v) {
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 1000) return v.toFixed(1)
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

function formatTime(t) {
  if (t == null) return '—'
  if (typeof t === 'number' && Number.isFinite(t)) {
    const d = new Date(t * 1000)
    return Number.isNaN(d.getTime()) ? String(t) : d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  }
  return String(t)
}

function outcomeLabel(sig) {
  if (!sig) return '—'
  if (sig.outcome === 'active') return sig.state === 'TP1' || sig.state === 'TP2' ? `Abierta (${sig.state}✓)` : 'Abierta'
  if (sig.outcome === 'win') return 'TP ✓'
  if (sig.outcome === 'loss') return 'SL ✗'
  return 'Timeout'
}

export default function PatronRupturaQuantumBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0.0',
  onHeaderMouseDown,
}) {
  const res = result && typeof result === 'object' ? result : null
  const patterns = Array.isArray(res?.patterns) ? res.patterns : []
  const active = res?.activeSignal ?? null
  const last = res?.lastSignal ?? null
  const stats = res?.stats ?? null
  const gated = res?.gated === true || res == null

  // Estado principal: señal viva > patrón formando > sin patrón.
  const stateLabel = active
    ? `${active.direction} activa`
    : patterns.length > 0 && !gated
      ? 'Patrón formando'
      : 'Sin patrón'
  const stateColor = active
    ? (active.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear)
    : patterns.length > 0 && !gated
      ? QUANTUM_COLORS.neutral
      : 'var(--ink-soft, #4A5A52)'

  // Distancia % al nivel más próximo (SL o TP1) desde la entrada.
  let nearestPct = null
  if (active && Number.isFinite(active.entry) && active.entry !== 0) {
    const dSl = Number.isFinite(active.sl) ? Math.abs(active.entry - active.sl) : Infinity
    const dTp1 = Number.isFinite(active.tp1) ? Math.abs(active.tp1 - active.entry) : Infinity
    const dMin = Math.min(dSl, dTp1)
    if (Number.isFinite(dMin)) nearestPct = (dMin / active.entry) * 100
  }

  const ctx = getChartContext() || {}
  const rowSymbol = typeof ctx.symbol === 'string' && ctx.symbol ? ctx.symbol : '—'
  const rowTf = typeof ctx.timeframe === 'string' && ctx.timeframe ? ctx.timeframe : '—'
  const last10 = Array.isArray(stats?.last10) ? [...stats.last10].reverse() : []
  // La tabla de últimas 10 respeta el toggle del panel (viaja en el result:
  // el badge no recibe config). Ausencia de la clave = visible (default true).
  const showLastSignals = res?.showLastSignals !== false

  // ── Sección "Patrón activo" (dashboard crudo del motor vía adapter) ──────
  // Decisión de visibilidad: solo con patrón formando/activo — se oculta con
  // señal activa (la sección de señal manda) y cuando el motor está escaneando
  // ('Scanning' = sin canal). Documentado en la ficha §5.
  const pinfo = res?.patternInfo ?? null
  const lastPattern = patterns.length > 0 ? patterns[patterns.length - 1] : null
  const showPatternSection = !active && pinfo && typeof pinfo.pattern === 'string' && pinfo.pattern !== 'Scanning'
  const bullProb = Number.isFinite(pinfo?.bullProb) ? pinfo.bullProb : 50
  // Fuerza 0-100: el motor no guarda strength por canal; se usa el de la
  // señal asociada al último patrón (null si no rompió) y, en su defecto, el
  // string del dashboard del motor ('Strong'/'Medium'/'Weak').
  const strengthScore = Number.isFinite(lastPattern?.strength) ? lastPattern.strength : null
  const strengthLabel = strengthScore != null
    ? (strengthScore < 40 ? 'Débil' : strengthScore <= 70 ? 'Media' : 'Fuerte')
    : pinfo?.strength === 'Strong' ? 'Fuerte'
      : pinfo?.strength === 'Medium' ? 'Media'
        : pinfo?.strength === 'Weak' ? 'Débil' : '—'
  const strengthColor = strengthLabel === 'Fuerte'
    ? QUANTUM_COLORS.bull
    : strengthLabel === 'Media'
      ? QUANTUM_COLORS.accent
      : strengthLabel === 'Débil'
        ? QUANTUM_COLORS.neutral
        : 'var(--ink-soft, #4A5A52)'

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          Patrón de Ruptura Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && (
            <span className="ml-1.5 text-[10px]" style={{ color: stateColor }}>· {stateLabel}</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {typeof onMinimize === 'function' && (minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        ))}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Estado</span>
              <span className="text-[11px] font-semibold" style={{ color: stateColor }}>{stateLabel}</span>
            </div>
          </div>

          {active && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1 grid grid-cols-2 gap-x-2 gap-y-1">
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Dirección</span>
                  <span className="text-[9px] font-medium" style={{ color: active.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>{active.direction}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Entrada</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{formatPrice(active.entry)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Nivel próximo</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{nearestPct != null ? `${nearestPct.toFixed(2)}%` : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Estado</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{outcomeLabel(active)}</span>
                </div>
              </div>
            </div>
          )}

          {showPatternSection && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1 flex items-center justify-between">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Patrón activo</span>
                <span className="text-[10px] font-medium" style={{ color: QUANTUM_COLORS.accent }}>{pinfo.pattern}</span>
              </div>
              <div className="flex flex-col gap-0.5 pb-1">
                <ScoreBar label="Bull" value={bullProb} color={QUANTUM_COLORS.bull} />
                <ScoreBar label="Bear" value={100 - bullProb} color={QUANTUM_COLORS.bear} />
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 pb-1">
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Fuerza</span>
                  <span className="text-[9px] font-medium" style={{ color: strengthColor }}>{strengthLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>R:R</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{pinfo.rr ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Toques</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{pinfo.touches ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Converg.</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{pinfo.convergence ?? '—'}</span>
                </div>
                <div className="flex justify-between col-span-2">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Contracción vol.</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{pinfo.volContraction ?? '—'}</span>
                </div>
              </div>
            </div>
          )}

          {stats && stats.totalSignals > 0 && (
            <div className="px-2.5 pb-2" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Historial</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Señales</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{stats.totalSignals}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Winrate</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{((stats.winrate ?? 0) * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP1</span>
                  <span className="text-[9px] font-medium" style={{ color: QUANTUM_COLORS.bull }}>{((stats.tp1Rate ?? 0) * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP2</span>
                  <span className="text-[9px] font-medium" style={{ color: QUANTUM_COLORS.bull }}>{((stats.tp2Rate ?? 0) * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP3</span>
                  <span className="text-[9px] font-medium" style={{ color: QUANTUM_COLORS.bull }}>{((stats.tp3Rate ?? 0) * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>SL</span>
                  <span className="text-[9px] font-medium" style={{ color: QUANTUM_COLORS.bear }}>{((stats.slRate ?? 0) * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Abiertas</span>
                  <span className="text-[9px] font-medium" style={{ color: QUANTUM_COLORS.neutral }}>{((stats.openRate ?? 0) * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>R medio</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{(stats.avgR ?? 0).toFixed(2)}R</span>
                </div>
                {stats.estimatedPnl != null ? (
                  <div className="flex justify-between">
                    <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>PnL est.</span>
                    <span className="text-[9px] font-medium" style={{ color: stats.estimatedPnl >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>
                      {stats.estimatedPnl >= 0 ? '+' : ''}{stats.estimatedPnl.toFixed(2)}
                    </span>
                  </div>
                ) : (stats.closedCount ?? 0) > 0 && (
                  // Sin budget el PnL del historial se muestra igualmente, en R.
                  <div className="flex justify-between">
                    <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>PnL</span>
                    <span className="text-[9px] font-medium" style={{ color: (stats.totalR ?? 0) >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>
                      {(stats.totalR ?? 0) >= 0 ? '+' : ''}{(stats.totalR ?? 0).toFixed(2)}R
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {last && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Última señal</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Dirección</span>
                  <span className="text-[9px] font-medium" style={{ color: last.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>{last.direction}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Resultado</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{outcomeLabel(last)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Entrada</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{formatPrice(last.entry)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>SL</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{formatPrice(last.sl)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP1</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{formatPrice(last.tp1)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP2</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{formatPrice(last.tp2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP3</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{formatPrice(last.tp3)}</span>
                </div>
                <div className="flex justify-between col-span-2">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Fecha</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{formatTime(last.time)}</span>
                </div>
              </div>
            </div>
          )}

          {showLastSignals && last10.length > 0 && (
            <div className="px-2.5 pb-2" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Últimas {last10.length}</span>
              </div>
              <div className="flex flex-col gap-0.5 max-h-[160px] overflow-y-auto">
                {last10.map(sig => (
                  <div key={sig.id} className="flex items-center justify-between gap-1">
                    <span className="text-[8px] whitespace-nowrap" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                      {formatTime(sig.time)} · {rowSymbol} · {rowTf}
                    </span>
                    <span className="text-[8px] font-medium whitespace-nowrap" style={{ color: sig.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>
                      {sig.direction} {formatPrice(sig.entry)}
                    </span>
                    <span className="text-[8px] whitespace-nowrap" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                      SL {formatPrice(sig.sl)} · TP {formatPrice(sig.tp1)}/{formatPrice(sig.tp2)}/{formatPrice(sig.tp3)}
                    </span>
                    <span
                      className="text-[8px] font-medium whitespace-nowrap"
                      style={{ color: sig.outcome === 'win' ? QUANTUM_COLORS.bull : sig.outcome === 'loss' ? QUANTUM_COLORS.bear : QUANTUM_COLORS.neutral }}
                    >
                      {outcomeLabel(sig)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// Barra de probabilidad estilo Régimen de SuperTrend Quantum
// (supertrend_quantum/Badge.jsx — track rgba(176,141,87,0.18)).
function ScoreBar({ label, value, color }) {
  const pct = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))
  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] w-8" style={{ color: 'var(--ink, #15211B)' }}>{label}</span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(176, 141, 87, 0.18)' }}>
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-[9px] w-9 text-right" style={{ color: 'var(--ink, #15211B)' }}>{pct.toFixed(0)}%</span>
    </div>
  )
}
